import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { applyHindi, restoreEnglish } from "../services/hindiTranslate";

const LanguageContext = createContext(null);
const STORAGE_KEY = "sf-lang";

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState(() => (localStorage.getItem(STORAGE_KEY) === "hi" ? "hi" : "en"));
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, language);
    document.documentElement.lang = language === "hi" ? "hi" : "en";
  }, [language]);

  useEffect(() => {
    if (language !== "hi") {
      restoreEnglish();
      setBusy(false);
      return undefined;
    }

    let alive = true;
    let timer = 0;
    const run = async () => {
      setBusy(true);
      await applyHindi(document.body);
      if (alive) setBusy(false);
    };
    const schedule = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        applyHindi(document.body);
      }, 140);
    };

    run();
    const observer = new MutationObserver(() => {
      if (!alive) return;
      schedule();
    });
    observer.observe(document.body, { subtree: true, childList: true, characterData: true });

    return () => {
      alive = false;
      observer.disconnect();
      window.clearTimeout(timer);
    };
  }, [language]);

  const value = useMemo(
    () => ({
      language,
      busy,
      toggleLanguage() {
        setLanguage((current) => (current === "hi" ? "en" : "hi"));
      },
    }),
    [busy, language]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const value = useContext(LanguageContext);
  if (!value) {
    throw new Error("useLanguage must be used inside LanguageProvider");
  }
  return value;
}
